import { useState } from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import CircularProgress from "components/circular-progress";
import { useGetPatientAlertsQuery } from "services/patient-alerts";
import type { PatientAlert } from "services/types/patient-alerts.type";

const ALERT_RED = "#E23B3B";
const ALERT_RED_DARK = "#C42E2E";
const ALERTS_PREVIEW_LIMIT = 25;

type PatientAlertChipProps = {
  patientId: number;
  count: number;
};

export default function PatientAlertChip({
  patientId,
  count
}: PatientAlertChipProps) {
  const [hovered, setHovered] = useState(false);

  const { data: alerts = [], isFetching } = useGetPatientAlertsQuery(
    {
      patient: patientId,
      limit: ALERTS_PREVIEW_LIMIT
    },
    {
      skip: !hovered || !patientId || patientId === -1
    }
  );

  const activeAlerts = (alerts as PatientAlert[]).filter(
    (alert) => !alert.discarded_at
  );

  const tooltipContent = (
    <Box sx={{ minWidth: 220, maxWidth: 360 }}>
      {isFetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={20} sx={{ py: 0 }} />
        </Box>
      ) : activeAlerts.length === 0 ? (
        <Typography variant="body2">No alerts</Typography>
      ) : (
        <Stack spacing={0.75} sx={{ py: 0.5 }}>
          {activeAlerts.map((alert, index) => (
            <Stack key={alert.id} direction="row" spacing={0.75}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {index + 1}.
              </Typography>
              <Typography variant="body2">
                {alert.alert_description || "No description"}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );

  return (
    <Tooltip
      arrow
      title={tooltipContent}
      onOpen={() => setHovered(true)}
      componentsProps={{
        tooltip: {
          sx: { backgroundColor: ALERT_RED_DARK, color: "#fff" }
        },
        arrow: {
          sx: { color: ALERT_RED_DARK }
        }
      }}
    >
      <Chip
        label={`${count} Alert${count === 1 ? "" : "s"}`}
        size="small"
        sx={{
          ml: 1,
          backgroundColor: ALERT_RED,
          color: "#fff",
          "&:hover": {
            backgroundColor: ALERT_RED_DARK
          }
        }}
      />
    </Tooltip>
  );
}
